import {HttpClient, HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpXsrfTokenExtractor} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {inject, Injector} from '@angular/core';
import {delay, of, switchMap, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {formatDistanceToNow, fromUnixTime} from 'date-fns';
import {ToastrService} from 'ngx-toastr';
import { LocalStorageService } from 'ngx-webstorage';
import {Router} from '@angular/router';
import {RedirectUrlService} from '../services/redirect-url-service/redirect-url.service';
import {STORAGE_KEYS} from '../constants/storage.constants';
import {AUTH_ROUTES} from '../constants/auth.constants';
import {TranslateService} from '@ngx-translate/core';

const NBSP_REGEX = /&nbsp;|[\u00A0\u202F]/gi;

const SENSITIVE_KEY_PATTERN = /password|token|secret|authorization|bearer|credential|api[_-]?key/i;

const stripNbspFromBody = (input: unknown): unknown => {
  if (typeof input === 'string') {
    const cleaned = input.replace(NBSP_REGEX, ' ');
    return cleaned === input ? input : cleaned;
  }
  if (Array.isArray(input)) {
    let changed = false;
    const mapped = input.map(item => {
      const next = stripNbspFromBody(item);
      if (next !== item) {
        changed = true;
      }
      return next;
    });
    return changed ? mapped : input;
  }
  if (input !== null && typeof input === 'object' && (input as object).constructor === Object) {
    let changed = false;
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      if (SENSITIVE_KEY_PATTERN.test(k)) {
        result[k] = v;
        continue;
      }
      const next = stripNbspFromBody(v);
      if (next !== v) {
        changed = true;
      }
      result[k] = next;
    }
    return changed ? result : input;
  }
  return input;
};

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const toastr = inject(ToastrService);
  const localStorageService = inject(LocalStorageService);
  const router = inject(Router);
  const redirectUrlService = inject(RedirectUrlService);
  const injector = inject(Injector);
  const xsrfExtractor = inject(HttpXsrfTokenExtractor);

  let delayDueToRateLimitUntil = new Date();

  const isAuthRequest = (url: string): boolean => {
    if (url.includes('/auth/')) {
      return true;
    }

    return AUTH_ROUTES.some(route => url.includes(route));
  };

  const handleUnauthenticatedResponseError = () => {
    const currentUrl = router.url;

    const isOnAuthRoute = AUTH_ROUTES.some(route => currentUrl.startsWith(route));

    if (isOnAuthRoute) {
      localStorageService.clear(STORAGE_KEYS.USER);
      document.cookie = 'XSRF-TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      return;
    }

    redirectUrlService.setRedirectUrl(currentUrl);

    localStorageService.clear(STORAGE_KEYS.USER);

    document.cookie = 'XSRF-TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

    // fallback to static strings if cyclic dep. detected
    try {
      const translate = injector.get(TranslateService);
      toastr.warning(translate.instant('auth.session_expired'));
    } catch {
      const locale = localStorageService.retrieve(STORAGE_KEYS.LOCALE) || 'de';
      const message = locale === 'en'
        ? 'Your session has expired. Please log in again.'
        : 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.';
      toastr.warning(message);
    }

    window.location.href = '/login';
  };

  if (req.url.includes(`${window.location.protocol}//${window.location.host}`)) {
    return next(req)
  }

  if (req.url.includes('i18n')) return next(req);

  if (req.url.startsWith('http') && !req.url.startsWith(environment.api.url)) {
    return next(req);
  } else if (!req.url.startsWith('http')) {
    req = req.clone({url: environment.api.url + req.url});
  }

  req = req.clone({
    withCredentials: true,
    headers: req.headers.set('accept-language', localStorageService.retrieve(STORAGE_KEYS.LOCALE) ?? 'de')
  });

  if (!isAuthRequest(req.url) && req.body !== null && req.body !== undefined) {
    const cleanedBody = stripNbspFromBody(req.body);
    if (cleanedBody !== req.body) {
      req = req.clone({body: cleanedBody});
    }
  }

  const hasXsrfCookie = (): boolean =>
    document.cookie.split(';').some(c => c.trim().startsWith('XSRF-TOKEN='));

  const handleResponseError = (error: HttpErrorResponse) => {
    if (error.status === 401 && !isAuthRequest(req.url)) {
      handleUnauthenticatedResponseError();
    }

    if (error.status === 429) {
      const reset_ts = error.headers.get('x-ratelimit-reset');
      if (reset_ts) {
        delayDueToRateLimitUntil = fromUnixTime(parseInt(reset_ts, 10));
        toastr.error('Rate limited! Please retry in: ' + formatDistanceToNow(delayDueToRateLimitUntil, {includeSeconds: true}));
      }
    }

    if ((error.status === 400 || error.status === 423) && error.error?.data?.error?.message) {
      toastr.error(error.error.data.error.message);
    }
  };

  const attachXsrfHeader = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
    const token = xsrfExtractor.getToken();
    if (token === null || request.headers.has('X-XSRF-TOKEN')) {
      return request;
    }
    return request.clone({headers: request.headers.set('X-XSRF-TOKEN', token)});
  };

  // fetch csrf token if cookie not present!
  if (!hasXsrfCookie() && !req.url.includes('/auth/csrf-cookie')) {
    const http = inject(HttpClient);

    return of(delayDueToRateLimitUntil).pipe(
      delay(delayDueToRateLimitUntil),
      switchMap(() => http.get('/auth/csrf-cookie', {observe: 'response'})),
      switchMap(() => next(attachXsrfHeader(req)).pipe(
        catchError((error: HttpErrorResponse) => {
          handleResponseError(error);
          return throwError(() => error);
        })
      ))
    );
  }

  return of(delayDueToRateLimitUntil).pipe(
    delay(delayDueToRateLimitUntil),
    switchMap(() => next(req)),
    catchError((error: HttpErrorResponse) => {
      handleResponseError(error);
      return throwError(() => error);
    })
  );
};
